import { DataGrid as MuiDataGrid, type DataGridProps as MuiDataGridProps } from '@mui/x-data-grid';
import { componentSizes } from '@/theme/componentSizes';
import { r, s } from '../../theme/tokens';
import { dataGridChromeSx } from './dataGridChrome';
import { getDataGridDefaults } from './dataGridDefaults';
import { DataGridEmptyState, DataGridLoadingState } from './dataGridStates';
import { useTheme } from './hooks';
import type { Theme } from '@mui/material/styles';
import type { ReactElement } from 'react';

export type DataGridProps = MuiDataGridProps & {
  emptyTitle?: string;
  emptyBody?: string;
  error?: boolean;
  errorTitle?: string;
  errorBody?: string;
  errorAction?: ReactElement;
};

export const DataGrid = ({
  sx,
  rowHeight,
  columnHeaderHeight,
  initialState,
  pageSizeOptions,
  slots,
  slotProps,
  disableColumnMenu,
  emptyTitle,
  emptyBody,
  error,
  errorTitle,
  errorBody,
  errorAction,
  ...rest
}: DataGridProps): ReactElement => {
  const theme = useTheme();
  const resolvedDisableColumnMenu = disableColumnMenu ?? true;
  const defaults = getDataGridDefaults({
    emptyTitle,
    emptyBody,
    error,
    errorTitle,
    errorBody,
    errorAction,
  });
  const mergedInitialState = {
    ...defaults.initialState,
    ...initialState,
    pagination: {
      ...defaults.initialState.pagination,
      ...initialState?.pagination,
      paginationModel: {
        ...defaults.initialState.pagination.paginationModel,
        ...initialState?.pagination?.paginationModel,
      },
    },
    sorting: { ...defaults.initialState.sorting, ...initialState?.sorting },
  };
  const resolvedPageSizeOptions = (pageSizeOptions ?? defaults.pageSizeOptions).filter(
    (value) => value !== 5,
  );

  const mergedSx = [
    dataGridChromeSx,
    {
      width: '100%',
      minWidth: 0,
      '--DataGrid-overlayHeight': `${componentSizes.dataGrid.stateMinHeight}px`,
      borderRadius: r('card'),
      border: 'none',
      backgroundColor: 'background.paper',
      '& .MuiDataGrid-columnHeaders': {
        backgroundColor: 'transparent',
      },
      '& .MuiDataGrid-columnHeader': {
        backgroundColor: 'transparent',
        minWidth: 0,
        px: s('sm'),
      },
      '& .MuiDataGrid-main': { minWidth: 0 },
      '& .MuiDataGrid-columnHeaderTitle': {
        fontFamily: (muiTheme: Theme) => muiTheme.typography.label.fontFamily,
        fontSize: (muiTheme: Theme) => muiTheme.typography.body.fontSize,
        fontWeight: (muiTheme: Theme) => muiTheme.typography.label.fontWeight,
        letterSpacing: (muiTheme: Theme) => muiTheme.typography.label.letterSpacing,
      },
      '& .MuiDataGrid-cell': {
        fontFamily: (muiTheme: Theme) => muiTheme.typography.body.fontFamily,
        fontSize: (muiTheme: Theme) => muiTheme.typography.body.fontSize,
        fontWeight: (muiTheme: Theme) => muiTheme.typography.body.fontWeight,
        lineHeight: (muiTheme: Theme) => muiTheme.typography.body.lineHeight,
        alignItems: 'center',
        justifyContent: 'flex-start',
        minWidth: 0,
        px: s('md'),
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      },
      '& .MuiDataGrid-cellContent': {
        fontSize: (muiTheme: Theme) => muiTheme.typography.body.fontSize,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        paddingLeft: s('sm'),
        paddingRight: s('sm'),
      },
      '& .MuiDataGrid-cell .MuiTypography-root, & .MuiDataGrid-cell .MuiLink-root': {
        fontSize: (muiTheme: Theme) => muiTheme.typography.body.fontSize,
      },
      '& .MuiDataGrid-sortButton, & .MuiDataGrid-menuIconButton': {
        backgroundColor: 'transparent !important',
      },
      '& .MuiDataGrid-sortButton:hover, & .MuiDataGrid-menuIconButton:hover': {
        backgroundColor: 'transparent !important',
      },
      '& .MuiDataGrid-iconButtonContainer': { backgroundColor: 'transparent !important' },
      '& .MuiDataGrid-columnHeader:focus-within, & .MuiDataGrid-cell:focus-within': {
        outline: 'none',
      },
      '& .MuiDataGrid-row.Mui-selected': { backgroundColor: 'transparent' },
      '& .MuiDataGrid-row.Mui-selected:hover': { backgroundColor: 'transparent' },
    },
    sx,
  ].filter(Boolean) as MuiDataGridProps['sx'];

  return (
    <MuiDataGrid
      autoHeight
      disableRowSelectionOnClick
      rowSelection={false}
      checkboxSelection={false}
      disableColumnMenu={resolvedDisableColumnMenu}
      rowHeight={rowHeight ?? theme.app.density.dataGrid.rowHeight}
      columnHeaderHeight={columnHeaderHeight ?? theme.app.density.dataGrid.headerHeight}
      pageSizeOptions={
        resolvedPageSizeOptions.length > 0 ? resolvedPageSizeOptions : defaults.pageSizeOptions
      }
      initialState={mergedInitialState}
      slots={{ ...defaults.slots, ...slots }}
      slotProps={{ ...defaults.slotProps, ...slotProps }}
      sx={mergedSx}
      {...rest}
    />
  );
};
export { DataGridEmptyState, DataGridLoadingState };
export { GridOverlay, type GridOverlayProps } from '@mui/x-data-grid';
